/**
 * Client Info Bar Component
 * Displays engagement client, contact, and branch information in a compact horizontal bar
 */

import { Building2, User, MapPin, Calendar, Download } from 'lucide-react'
import type { EngagementInfo } from '@/types/ui/dashboard'
import { useFormatters } from '@/hooks/use-formatters'
import { Button } from '@/components/ui/button'
import { exportToPDF, generateFilename } from '@/lib/utils/export-utils'
import { parseDateOnly } from '@/lib/utils'

interface ClientInfoBarProps {
  engagementInfo: EngagementInfo | null
  engagementId: string | number
}

export function ClientInfoBar({ engagementInfo, engagementId }: ClientInfoBarProps) {
  const { formatDate } = useFormatters()
  
  const handleExportPDF = async () => {
    try {
      const filename = generateFilename('EngagementDashboard', engagementId, 'pdf')
      await exportToPDF('analytics-dashboard', filename)
    } catch (error) {
      console.error('Failed to export PDF:', error)
    }
  }
  
  return (
    <div className="flex items-center gap-6 px-4 py-2.5 bg-muted/50 rounded-md border">
      <div className="flex items-center gap-2">
        <Building2 className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Client:</span>
        <span className="text-sm font-medium">{engagementInfo?.client || '—'}</span>
      </div>
      
      <div className="h-4 w-px bg-border" />
      
      <div className="flex items-center gap-2">
        <User className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Engagement Lead:</span>
        <span className="text-sm font-medium">{engagementInfo?.contact || '—'}</span>
      </div>
      
      <div className="h-4 w-px bg-border" />
      
      <div className="flex items-center gap-2">
        <MapPin className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Branch:</span>
        <span className="text-sm font-medium">{engagementInfo?.branch || '—'}</span>
      </div>
      
      <div className="h-4 w-px bg-border" />
      
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Start:</span>
        <span className="text-sm font-medium">{formatDate(parseDateOnly(engagementInfo?.startDate))}</span>
      </div>
      
      <div className="h-4 w-px bg-border" />
      
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">End:</span>
        <span className="text-sm font-medium">{formatDate(parseDateOnly(engagementInfo?.endDate))}</span>
      </div>
      
      <div className="flex-1" />
      
      <Button variant="outline" size="sm" onClick={handleExportPDF}>
        <Download className="w-3.5 h-3.5 mr-1.5" />
        Export PDF
      </Button>
    </div>
  )
}

